<template>
    <div>
        <div class="mb-3">
            <b-button variant="primary" @click="exportToPDF">
                <i class="mdi mdi-download me-1"></i>Export to PDF
            </b-button>
        </div>
        <div ref="orgChartDiv">
            <org-chart @selectedNode="selectedNodeEvent"
                @contextAction="contextActionEvent"
                v-if="hasMounted" allow_toggle
                has_context_menu
                :level_data="chart_config">
            </org-chart>
        </div>

        <b-modal v-show="show_modal" id="levelDetailsModal" :title="level_details.type">
        <b-overlay :show="is_saving">
            <b-form>
                <b-row>
                    <b-col cols="12">
                        <label for="">Level Name</label>
                        <b-form-input v-model="level_details.name"></b-form-input>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" class="mt-3">
                        <label for="">Level Type</label>
                        <b-form-select class="form-select" v-model="level_details.level_type_id" :options="level_type_options"></b-form-select>
                    </b-col>
                </b-row>
            </b-form>
            
        </b-overlay>
        <template #modal-footer>
            <div class="w-100">
                <b-button
                    :disabled="!canSubmit"
                    class="ms-2"
                    variant="primary"
                    style="float: right;"
                    @click="saveFormData">
                    Save
                </b-button>

                <b-button
                    style="float: right;"
                    @click="$bvModal.hide('levelDetailsModal')">
                    Close
                </b-button>
            </div>
        </template>
        </b-modal>
        
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import { clientAdminState, clientAdminMethods } from '@/state/helpers'
import _ from 'lodash'
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
export default {
    data:() => ({
        is_saving: false,
        chart_config: null,
        selected_level_id: null,
        hasMounted: false,

        show_modal: false,

        level_details:{
            id: -1,
            name: '',
            parent_id: null,
            level_type_id: -1,
            type: '', //Add/Edit - used for modal title
        }

    }),
    computed:{
        ...clientAdminState,
        canSubmit(){
            if(this.level_details.name !== '' && this.level_details.level_type_id != -1){
                return true;
            }
            else{
                return false;
            }
        },
        level_type_options(){
            if(this.level_types == null){
                return [];
            }
            else{
                let opts = [
                    {
                        value: -1,
                        text: 'Select Level Type'
                    }
                ];
                this.level_types.forEach( item => {
                    opts.push({
                        value: item.id,
                        text: item.name,
                    })
                })
                return opts;
            }
        }
    },
    methods:{
        ...clientAdminMethods,
        async exportToPDF(){
            try {
                // Show loading message
                Swal.fire({
                    title: 'Generating PDF...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                // Check if currently in compact/stacked view
                const orgChartComponent = this.$children.find(child => child.$options.name === 'org-chart' || child.stacked !== undefined);
                const wasStacked = orgChartComponent ? orgChartComponent.stacked : false;

                // If not in compact view, switch to it
                if (orgChartComponent && !wasStacked) {
                    orgChartComponent.toggleView();
                    // Wait for the view to fully render
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }

                const element = this.$refs.orgChartDiv;

                // Get the actual tree-chart container
                const treeChart = element.querySelector('#tree-chart');
                const treantContainer = treeChart || element;

                // Find all nodes and connectors to calculate the actual bounding box
                const allNodes = treantContainer.querySelectorAll('.node');
                const svg = treantContainer.querySelector('svg');
                let maxRight = 0;
                let maxBottom = 0;

                allNodes.forEach(node => {
                    const rect = node.getBoundingClientRect();
                    const containerRect = treantContainer.getBoundingClientRect();
                    const right = rect.right - containerRect.left;
                    const bottom = rect.bottom - containerRect.top;
                    maxRight = Math.max(maxRight, right);
                    maxBottom = Math.max(maxBottom, bottom);
                });

                // Check SVG dimensions if available (for connectors)
                if (svg) {
                    const svgRect = svg.getBoundingClientRect();
                    const containerRect = treantContainer.getBoundingClientRect();
                    maxRight = Math.max(maxRight, svgRect.right - containerRect.left);
                    maxBottom = Math.max(maxBottom, svgRect.bottom - containerRect.top);
                }

                // Add very generous padding
                const padding = 300;
                const fullWidth = Math.ceil(maxRight + padding);
                const fullHeight = Math.ceil(maxBottom + padding);

                console.log('Capturing dimensions:', fullWidth, 'x', fullHeight);
                console.log('Max right:', maxRight, 'Max bottom:', maxBottom);

                // Temporarily set container to fixed large size to ensure everything renders
                const originalOverflow = treantContainer.style.overflow;
                const originalWidth = treantContainer.style.width;
                const originalHeight = treantContainer.style.height;

                treantContainer.style.overflow = 'visible';
                treantContainer.style.width = fullWidth + 'px';
                treantContainer.style.height = fullHeight + 'px';

                // Wait a bit for layout to settle
                await new Promise(resolve => setTimeout(resolve, 100));

                // Capture the org chart as canvas with higher quality
                const canvas = await html2canvas(treantContainer, {
                    scale: 2,
                    useCORS: true,
                    logging: true,
                    backgroundColor: '#ffffff',
                    scrollX: 0,
                    scrollY: 0
                });

                // Restore original styles
                treantContainer.style.overflow = originalOverflow;
                treantContainer.style.width = originalWidth;
                treantContainer.style.height = originalHeight;

                const imgData = canvas.toDataURL('image/png');
                const imgWidth = canvas.width;
                const imgHeight = canvas.height;

                // Create PDF - always use landscape for org charts
                const pdf = new jsPDF('landscape', 'mm', 'a4');

                // Get PDF dimensions with margins
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                const margin = 10; // 10mm margins on all sides

                // Calculate scaling to fit the image on the page with margins
                const availableWidth = pdfWidth - (margin * 2);
                const availableHeight = pdfHeight - (margin * 2);

                const widthRatio = availableWidth / imgWidth;
                const heightRatio = availableHeight / imgHeight;
                const ratio = Math.min(widthRatio, heightRatio);

                const scaledWidth = imgWidth * ratio;
                const scaledHeight = imgHeight * ratio;

                // Center the image on the page
                const xOffset = (pdfWidth - scaledWidth) / 2;
                const yOffset = (pdfHeight - scaledHeight) / 2;

                pdf.addImage(imgData, 'PNG', xOffset, yOffset, scaledWidth, scaledHeight);
                pdf.save('organogram.pdf');

                // Restore original view if we changed it
                if (orgChartComponent && !wasStacked) {
                    orgChartComponent.toggleView();
                }

                Swal.close();

                Swal.fire({
                    toast: true,
                    position: 'top',
                    iconColor: 'white',
                    icon: 'success',
                    title: 'PDF exported successfully',
                    customClass: { popup: 'success-toast' },
                    showConfirmButton: false,
                    timer: 2000,
                });
            } catch (error) {
                console.error('Error generating PDF:', error);
                Swal.fire({
                    toast: true,
                    position: 'top',
                    iconColor: 'white',
                    icon: 'error',
                    title: 'Failed to export PDF',
                    customClass: { popup: 'error-toast' },
                    showConfirmButton: false,
                    timer: 3000,
                });
            }
        },
        selectedNodeEvent(id){
            if(id == this.selected_level_id){
                this.selected_level_id = null;
                this.$emit('level_id', null);
            }
            else{
                this.selected_level_id = id;
                this.$emit('level_id', id);
            }
        },
        contextActionEvent(payload){
            //* get id from actual elements id eg treeNode-69
            let id = payload.id.split('-');
            id = id[1];

            switch(payload.action_type){
                case 'add':{
                    //* id used as parent id
                    this.addNewLevel(id)
                    break;
                }
                case 'add-sibling':{
                    this.addSiblingLevel(id)
                    break;
                }
                case 'edit':{
                    this.editLevelItem(id)
                    break;
                }
                case 'delete':{
                    this.deleteLevelItem(id)
                    break;
                }
                default:{
                    break;
                }
            }
        },
        loadChartData(){
            //if(this.$refs['orgChartDiv']){
            //    let height = this.$refs['orgChartDiv'].clientHeight;
            //    this.$refs['orgChartDiv'].style.height = `${height}px`
            //}
            this.hasMounted = false;
            let levels_data = _.clone(this.user_levels);

            this.chart_config = levels_data;
            this.$nextTick(()=>{
                this.hasMounted = true;
                //this.$nextTick(()=>{
                //    this.$refs['orgChartDiv'].style.height = `unset`
                //})
            })
        },
        addNewLevel(parent_id){
            this.level_details = {
                id: -1,
                name: '',
                parent_id: parent_id,
                level_type_id: -1,
                type: 'Add Child Level'
            }
            this.showModal();
        },
        addSiblingLevel(current_id){
            //* check if right clicked item doesn't = 0  -- can only be 1 main parent
            let level = this.user_levels.find( lvl => { return Number(current_id) == lvl.id });
            if(level){
                if(level.parent_id == 0){
                    Swal.fire('There can only be one top level parent');
                    return;
                }

                this.level_details = {
                    id: -1,
                    name: '',
                    parent_id: level.parent_id,
                    level_type_id: -1,
                    type: 'Add Child Level'
                }
                this.showModal();
            }
        },
        editLevelItem(current_id){
            let index = _.findIndex(this.user_levels, {id: Number(current_id)});
            if(index != -1){
                this.level_details = {
                    id: current_id,
                    name: this.user_levels[index].name,
                    level_type_id: this.user_levels[index].level_type_id,
                    //* if parent id == 0 then set to null for saving
                    parent_id: this.user_levels[index].parent_id == 0 ? null : this.user_levels[index].parent_id,
                    type: 'Edit Level'
                }
                this.showModal();
            }
        },
        deleteLevelItem(current_id){
            let index = _.findIndex(this.user_levels, {id: Number(current_id)});
            if(index != -1){
                let level_title = this.user_levels[index].name;
                Swal.fire({
                    title: `Delete level '${level_title}'?`,
                    text: "Warning! This can't be undone",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonColor: "#34c38f",
                    cancelButtonColor: "#f46a6a",
                    confirmButtonText: "Yes",
                }).then((result) => {
                    if (result.value) {
                        this.is_saving = true;
                        this.deleteLevel({id: current_id})
                        .then(()=>{
                            this.loadLevels()
                            .then(()=>{
                                this.is_saving = false;
                                this.loadChartData();
                            })
                            Swal.fire({
                                toast: true,
                                position: 'top',
                                iconColor: 'white',
                                icon: 'success',
                                title:`Deleted '${level_title}'`,
                                customClass: {     popup: 'success-toast' },
                                showConfirmButton: false,
                                timer: 1500,
                                //timer: 2000, timerProgressBar: true
                            });
                        })
                        .catch((error_message)=>{
                            Swal.fire({
                                toast: true,
                                position: 'top',
                                iconColor: 'white',
                                icon: 'warning',
                                title:`${error_message}`,
                                customClass: {     popup: 'error-toast' },
                                showConfirmButton: false,
                                timer: 5000,
                                timerProgressBar: true
                            });
                        })
                    }
                });
            }
        },
        saveFormData(){
            if(this.canSubmit){
                this.is_saving = true;
                let params = {
                    ...this.level_details,
                }
                delete params.type;//* remove unused property
                this.saveLevel(params)
                .then(()=>{
                    this.loadLevels()
                    .then(()=>{
                        this.is_saving = false;
                        this.loadChartData();
                        this.$bvModal.hide('levelDetailsModal');
                    })
                })
                .catch(()=>{
                    alert('Err')
                })
            }
        },
        showModal(){
            this.show_modal = true;
            this.$nextTick(()=>{
                this.$bvModal.show('levelDetailsModal');
                    this.$root.$once('bv::modal::hidden',(event)=>{
                        if(event.type == 'hidden' && event.componentId == 'levelDetailsModal'){
                            this.show_modal = false;
                        }
                    })
            })
        },
    },
    mounted(){
        //TODO - should maybe only load this when the modal shown
        if(this.level_types == null){
            this.loadLevelTypes();
        }

        if(this.user_levels == null){
            this.loadLevels()
            .then(()=>{
                this.loadChartData();
            })
        }
        else{
            this.loadChartData();
        }
        
    },
}
</script>

<style>

</style>